import { useReducer, useState } from 'react';
import { useDebounce } from 'react-use';

import { TextSkeleton } from '@/components/Skeleton';

import { fontStyles } from '@/styles/typography';

import { Pagination } from '@/ui/Pagination';

import { classNames } from '@/utils/classnames';

import { usePagination } from '../../../hooks/usePagination';
import { useSourcesAll } from '../../../hooks/useSourcesAll';
import { LiquiditySourcesFilters } from '../LiquiditySourcesFilters';
import {
  FILTER_ACTIONS,
  filtersReducer,
  initialFiltersState,
} from '../LiquiditySourcesFilters/filtersReducer';
import { LiquiditySourcesModal } from '../LiquiditySourcesModal';
import { LiquiditySourceRow } from './LiquiditySourceRow';

const PAGE_SIZE = 8;
const LOADING_CELLS: { width: number }[][] = new Array(PAGE_SIZE).fill([
  { width: 102 },
  { width: 86 },
  { width: 51 },
  { width: 102 },
]);

export function LiquiditySourcesTable() {
  const paginationProps = usePagination({ pageSize: PAGE_SIZE });
  const [filterState, dispatch] = useReducer(filtersReducer, initialFiltersState);
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');
  const [selectedSource, setSelectedSource] = useState('');
  const { isLoading, liquiditySources, totalSources } = useSourcesAll({
    time: filterState.time,
    searchTerm: debouncedSearchTerm,
    chainIds: filterState.networks,
    page: paginationProps.currentPage,
  });

  useDebounce(() => setDebouncedSearchTerm(filterState.searchTerm), 250, [filterState.searchTerm]);

  const clearSource = () => setSelectedSource('');

  const selectedSourceValues = liquiditySources.find(
    (source) => selectedSource === source.liquiditySource,
  );

  return (
    <section className="rounded-2xl border border-token-gray-300 mb-18 mx-auto max-w-[1240px] overflow-x-auto min-h-[540px]">
      <div className="ml-8 mr-5 my-[18px] relative flex tablet:justify-between flex-wrap gap-4 items-center">
        <h5 className={fontStyles({ variant: 'H5', class: ['tablet:inline-block'] })}>
          Integrated liquidity sources
        </h5>
        <LiquiditySourcesFilters
          values={filterState}
          onChange={(filterState) => {
            paginationProps.setCurrentPage(1);
            dispatch({ type: FILTER_ACTIONS.UPDATE_ALL_FILTERS, payload: filterState });
          }}
        />
      </div>

      <table className="w-full mt-4.5 overflow-hidden">
        <thead
          className={fontStyles({
            variant: 'BODY_MD',
            class: [
              'text-token-gray-800',
              'font-normal',
              'text-left',
              'border-y',
              'border-y-special-border-divider',
            ],
          })}
        >
          <tr className="text-left">
            <th className="align-middle font-normal text-350 py-3 min-w-[160px] desktop:min-w-[294px] pl-8">
              Source
            </th>
            <th className="align-middle font-normal text-350 py-3 min-w-[160px] desktop:min-w-[294px]">
              Total Volume (USD)
            </th>
            <th className="align-middle font-normal text-350 py-3 min-w-[160px] desktop:min-w-[294px]">
              30D Volume (USD)
            </th>
            <th className="align-middle font-normal text-350 py-3 min-w-[160px] desktop:min-w-[294px]">
              Networks available
            </th>
          </tr>
        </thead>
        <tbody>
          {isLoading &&
            LOADING_CELLS.map((cell, idx) => (
              <tr
                key={idx}
                className="font-normal text-sm text-token-gray-800 border-b border-solid border-token-gray-300 h-12"
              >
                {cell.map(({ width }, idxItem) => (
                  <td
                    key={idxItem}
                    className={classNames('align-middle', { 'pl-8': idxItem === 0 })}
                  >
                    <TextSkeleton width={width} height={18} />
                  </td>
                ))}
              </tr>
            ))}
          {!liquiditySources.length && !isLoading ? (
            <tr>
              <td
                colSpan={4}
                className="text-center absolute left-1/2 flex items-center -translate-x-1/2 h-[380px]"
              >
                No liquidity sources
                {filterState.searchTerm && ` called "${filterState.searchTerm}"`}
              </td>
            </tr>
          ) : (
            liquiditySources.map((source) => (
              <LiquiditySourceRow
                key={source.liquiditySource}
                setSelectedSource={setSelectedSource}
                {...source}
              />
            ))
          )}
        </tbody>
      </table>
      <div className="px-8 py-4">
        {!!liquiditySources.length && (
          <Pagination {...paginationProps} lastPage={Math.ceil(totalSources / PAGE_SIZE)} />
        )}
      </div>

      {!!selectedSourceValues && (
        <LiquiditySourcesModal
          isOpen={!!selectedSource}
          onClose={clearSource}
          onOpenChange={clearSource}
          selectedSource={selectedSourceValues}
        />
      )}
    </section>
  );
}
